import React, { FC } from "react";
import { Modal } from "../../widgets/modal";
import Loader from "../../widgets/Loader";
import { moveFromListScheduled } from "./scheduling.utils";
import Swal from "sweetalert2";
import { SCHEDULING_DIALOGS } from "../../constants/dialog.constants";

type SchedulingMoveFromListModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  scheduledId: number;
  propertyName?: string;
};

type SchedulingActionResponse = {
  messages?: Array<{
    code?: string | number;
    message?: string;
  }>;
  status?: string;
  message?: string;
};

const getResponseObject = (result: unknown) =>
  typeof result === "object" && result !== null
    ? (result as SchedulingActionResponse)
    : undefined;

const isSuccessfulResponse = (result: unknown) => {
  const response = getResponseObject(result);

  return (
    result === "Success" ||
    response?.messages?.[0]?.code === "0" ||
    response?.messages?.[0]?.code === 0 ||
    response?.status === "success"
  );
};

const getFailureMessage = (result: unknown) => {
  const response = getResponseObject(result);

  return (
    response?.messages?.[0]?.message ||
    response?.message ||
    SCHEDULING_DIALOGS.moveFromListFailed
  );
};

const SchedulingMoveFromListModal: FC<SchedulingMoveFromListModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  scheduledId,
  propertyName,
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleConfirmMoveFromList = async () => {
    if (!scheduledId) {
      Swal.fire({
        customClass: ".swal2-confirm",
        icon: "warning",
        title: SCHEDULING_DIALOGS.moveFromListMissingJob,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await moveFromListScheduled(scheduledId);

      if (isSuccessfulResponse(result)) {
        await Swal.fire({
          customClass: {
            popup: "sweetAlert",
          },
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
          title: SCHEDULING_DIALOGS.moveFromListSuccess,
        });
        await onSubmit();
        onClose();
      } else {
        Swal.fire({
          customClass: ".swal2-confirm",
          icon: "warning",
          title: getFailureMessage(result),
        });
      }
    } catch (moveError) {
      console.error(SCHEDULING_DIALOGS.moveFromListFailed, moveError);
      Swal.fire({
        customClass: ".swal2-confirm",
        icon: "warning",
        title: SCHEDULING_DIALOGS.moveFromListFailed,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      label={`${SCHEDULING_DIALOGS.moveFromListLabel}${propertyName ? ` (${propertyName})` : ""}`}
      onClose={onClose}
      className="custom-modal scheduling-ready-for-mulch-modal scheduling-status-confirmation-modal scheduling-move-from-list-modal"
    >
      <div className="requestModalSection">
        <div className="modalForm">
          {isSubmitting ? <Loader /> : null}
          <div className="scheduling-description-content">
            <div className="scheduling-description-block">
              <div className="scheduling-description-text scheduling-status-popup-text">
                {SCHEDULING_DIALOGS.confirmMoveFromList}
              </div>
            </div>
          </div>
          <div className="scheduling-priority-footer">
            <div className="scheduling-modal-actions scheduling-priority-actions">
              <button
                type="button"
                className="btn cancelPwdBtn"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {SCHEDULING_DIALOGS.cancelButton}
              </button>
              <button
                type="button"
                className="btn submitBtn"
                onClick={handleConfirmMoveFromList}
                disabled={isSubmitting}
              >
                {SCHEDULING_DIALOGS.confirmButton}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SchedulingMoveFromListModal;
